import { useState } from 'react'
import { useQuery, useApolloClient } from '@apollo/client'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { mutations, queries } from '../../../graphql'
import {
  Acknowledge,
  BlockQuote,
  Button,
  CardBox,
  Container,
  Fields,
  InputAdornment,
  NotificationDialog,
  Stack,
  Text,
  Scheduled,
  Recurring,
  useAlert,
} from '../../../components'
import { fromAmountField, getTrackingData } from '../../../utils'

export function ETransfer() {
  const { fundId } = useParams()
  const { data: { me: { fund } = { fund: {} } } = {} } = useQuery(queries.funds.myFund, {
    variables: { fundId: Number(fundId) },
    skip: !fundId,
  })
  const { number, fundProfile } = fund
  const portalName = fundProfile?.portalName
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formData, setFormData] = useState({})
  const client = useApolloClient()

  const {
    register,
    handleSubmit,
    control,
    formState,
    setValue,
    getValues,
    reset: resetForm,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      amount: 0.0,
      isRecurring: false,
      recurringInterval: 'monthly',
      processingDate: null,
    },
  })

  const closeSuccessDialog = () => {
    resetForm()
    setDialogOpen(false)
  }

  const onSubmit = async ({ amount, isRecurring, recurringInterval, processingDate }) => {
    try {
      const description = 'e-Transfer'

      await client.mutate({
        mutation: mutations.contributions.createContributionRecord,
        variables: {
          fundId: Number(fundId),
          amount: fromAmountField(amount),
          paymentType: 3,
          description,
          taxReceipted: true,
          charityId: '',
          usedGiveGenius: false,
          isRecurring,
          recurringInterval,
          processingDate,
          ...getTrackingData(),
        },
        refetchQueries: [
          { query: queries.funds.myFund, variables: { fundId: Number(fundId) } },
          { query: queries.funds.myFundActivity, variables: { fundId: Number(fundId) } },
          { query: queries.paymentMethods.myLinkedBanks, variables: { fundId: Number(fundId) } },
          ...(isRecurring ? [{ query: queries.funds.myRecurring, variables: { fundId: Number(fundId) } }] : []),
        ],
        awaitRefetchQueries: true,
      })

      setFormData({ amount, isRecurring, recurringInterval, processingDate })
      setDialogOpen(true)
    } catch (e) {
      setAlert({
        message: `Failed to create a contribution: ${e.message ?? ''}`,
        error: e.message,
        severity: 'error',
      })
    }
  }

  const { errors = {} } = formState

  return (
    <Container maxWidth="lg">
      <Stack spacing={0}>
        <CardBox title="E-Transfer">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <Fields.Text
                label="Amount"
                name="amount"
                error={!!errors?.amount}
                helperText={errors?.amount?.message}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputComponent: Fields.Amount,
                  inputProps: {
                    setValue: (value) => setValue('amount', value),
                    getValue: () => getValues('amount'),
                    defaultValue: 0,
                  },
                }}
                {...register('amount', {
                  required: 'This field is required',
                  validate: {
                    positive: (value) => parseFloat(value) > 0 || 'Must be a positive number',
                    min: (value) => parseFloat(value) >= 1 || 'Must be at least 1',
                  },
                })}
              />

              <Scheduled
                register={register}
                errors={errors}
                control={control}
                getValues={getValues}
                setValue={setValue}
                entity="e-Transfer"
              />

              <Recurring register={register} errors={errors} getValues={getValues} setValue={setValue} />

              <Text.Body>
                You can perform a simple transaction from your online banking platform. Simply send an e-Transfer using
                your bank&apos;s online platform, choose the amount, and send it to:
              </Text.Body>
              <BlockQuote>
                <Text.Bold>Recipient</Text.Bold>
                <Text.Body>
                  GiveWise Canada <br />
                  donations@givewise.ca
                </Text.Body>
                <Text.Bold>Notes</Text.Bold>
                <Text.Body>
                  Giving Fund #{number} <br />
                  Donor Name: {portalName}
                </Text.Body>
              </BlockQuote>
              <Text.Body>
                <strong>*IMPORTANT:</strong> In the notes section, you must remember to enter your first and last name
                and your GiveWise Giving Fund Number, so we can properly receive your funds into the correct Giving
                Fund.
              </Text.Body>

              {(getValues('isRecurring') || getValues('processingDate')) && (
                <Text.Body>
                  <strong>Note:</strong>{' '}
                  {getValues('isRecurring') &&
                    "For recurring e-Transfers, you will need to set up the recurring schedule in your bank's online platform. "}
                  {getValues('processingDate') &&
                    'For scheduled e-Transfers, please ensure you send the e-Transfer on the scheduled date.'}
                </Text.Body>
              )}

              <Button type="submit" sx={{ alignSelf: 'flex-end' }}>
                Submit
              </Button>
              <Alert {...alertProps} sx={{ alignSelf: 'flex-end' }} />
            </Stack>
          </form>
        </CardBox>
      </Stack>
      <NotificationDialog
        open={dialogOpen}
        onClose={closeSuccessDialog}
        type="success"
        title="Success!"
        message={
          <>
            <p>You have successfully initiated a contribution.</p>
            <p>
              You will now need to create an e-Transfer using your bank&apos;s online platform in the amount of $
              {formData.amount} and send it to:
            </p>
            <p>
              <strong>Recipient:</strong>
              <br />
              GiveWise Canada
              <br />
              donations@givewise.ca
            </p>
            <p>
              <strong>Include in &quot;Message For Recipient&quot;:</strong>
              <br />
              Giving Fund #{number}
              <br />
              Donor Name: {portalName}
            </p>
            {formData.isRecurring && (
              <p>
                <strong>Recurring:</strong> This contribution will recur {formData.recurringInterval}. Please set up a
                recurring e-Transfer in your bank&apos;s platform or manually send the e-Transfer according to your
                selected schedule.
              </p>
            )}
            {formData.processingDate && (
              <p>
                <strong>Scheduled:</strong> This contribution is scheduled for{' '}
                {new Date(formData.processingDate).toLocaleDateString()}. Please ensure you send the e-Transfer on this
                date.
              </p>
            )}
          </>
        }
        actions={[
          {
            label: 'Done',
            onClick: closeSuccessDialog,
            to: `/funds/${fundId}/activity/giving-wallet`,
          },
        ]}
      />
    </Container>
  )
}
